import { useEffect, useState } from 'react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

/**
 * The global comms mode from graphy's COMMS_MODE switch.
 * 'full_supabase' — every worker is on Supabase; Chat surfaces route to
 * chat-v2. 'cohort' — the allowlist decides per worker (today's behavior).
 */
export type CommsMode = 'cohort' | 'full_supabase';

interface ModeResponse {
  success: boolean;
  data?: { mode?: string };
}

// Module-level cache: the mode is fixed for the lifetime of the deploy, so
// one fetch serves every consumer (both sidebars + bottom nav) and re-mounts.
let cachedMode: CommsMode | null = null;
let inflight: Promise<CommsMode> | null = null;

async function fetchCommsMode(): Promise<CommsMode> {
  try {
    const res = await axiosRubyClient.get<ModeResponse>('/api/admin/comms/mode');
    // Fail-safe: only an explicit, well-formed 'full_supabase' flips the UI;
    // anything else (error page, old graphy without the endpoint) is cohort.
    return res.data?.success === true && res.data.data?.mode === 'full_supabase'
      ? 'full_supabase'
      : 'cohort';
  } catch {
    return 'cohort';
  }
}

export function __resetCommsModeForTests(): void {
  cachedMode = null;
  inflight = null;
}

/**
 * Resolves the global comms mode once per page load. Returns 'cohort'
 * until (and unless) graphy definitively answers 'full_supabase', so a
 * fetch failure leaves every Chat surface exactly as it is today.
 */
export function useCommsMode(): CommsMode {
  const [mode, setMode] = useState<CommsMode>(cachedMode ?? 'cohort');

  useEffect(() => {
    if (cachedMode !== null) return undefined;
    inflight ??= fetchCommsMode().then((resolved) => {
      cachedMode = resolved;
      return resolved;
    });
    let cancelled = false;
    void inflight.then((resolved) => {
      if (!cancelled) setMode(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return mode;
}
